/**
 * Web Audio API tone generator for completion notifications.
 * No audio files — all sounds are generated programmatically.
 */

export type NotificationSoundId = 'none' | 'chime' | 'ping' | 'bell' | 'success';

export const NOTIFICATION_SOUNDS: { id: NotificationSoundId; label: string }[] = [
  { id: 'none', label: 'None' },
  { id: 'chime', label: 'Chime' },
  { id: 'ping', label: 'Ping' },
  { id: 'bell', label: 'Bell' },
  { id: 'success', label: 'Success' },
];

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  // Resume if suspended (browser autoplay policy)
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

function playTone(frequency: number, duration: number, startTime: number, ctx: AudioContext, gain: GainNode) {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, startTime);
  osc.connect(gain);
  osc.start(startTime);
  osc.stop(startTime + duration);
}

function playSoundImpl(soundId: NotificationSoundId): void {
  if (soundId === 'none') return;

  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.connect(ctx.destination);
  masterGain.gain.setValueAtTime(0.3, ctx.currentTime);

  const now = ctx.currentTime;

  switch (soundId) {
    case 'chime': {
      // C5 → E5 two-note chime
      const g1 = ctx.createGain();
      g1.connect(masterGain);
      g1.gain.setValueAtTime(0.6, now);
      g1.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      playTone(523.25, 0.4, now, ctx, g1); // C5

      const g2 = ctx.createGain();
      g2.connect(masterGain);
      g2.gain.setValueAtTime(0.6, now + 0.15);
      g2.gain.exponentialRampToValueAtTime(0.01, now + 0.55);
      playTone(659.25, 0.4, now + 0.15, ctx, g2); // E5
      break;
    }
    case 'ping': {
      // Short 1047Hz ping
      const g = ctx.createGain();
      g.connect(masterGain);
      g.gain.setValueAtTime(0.5, now);
      g.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
      playTone(1047, 0.2, now, ctx, g);
      break;
    }
    case 'bell': {
      // 880Hz resonant bell
      const g = ctx.createGain();
      g.connect(masterGain);
      g.gain.setValueAtTime(0.5, now);
      g.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
      playTone(880, 0.8, now, ctx, g);

      // Add harmonic for resonance
      const g2 = ctx.createGain();
      g2.connect(masterGain);
      g2.gain.setValueAtTime(0.2, now);
      g2.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
      playTone(1760, 0.6, now, ctx, g2);
      break;
    }
    case 'success': {
      // C5 → E5 → G5 ascending triad
      const notes = [523.25, 659.25, 783.99];
      notes.forEach((freq, i) => {
        const g = ctx.createGain();
        g.connect(masterGain);
        const start = now + i * 0.12;
        g.gain.setValueAtTime(0.5, start);
        g.gain.exponentialRampToValueAtTime(0.01, start + 0.35);
        playTone(freq, 0.35, start, ctx, g);
      });
      break;
    }
  }
}

export function playNotificationSound(soundId: NotificationSoundId): void {
  try {
    playSoundImpl(soundId);
  } catch {
    // Audio not available — silent fallback
  }
}

export function previewNotificationSound(soundId: NotificationSoundId): void {
  playNotificationSound(soundId);
}
